<template>
  <div
    ref="triggerWrapper"
    class="relative inline-block cursor-default"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <slot name="trigger" />

    <div
      v-if="showTooltip"
      ref="floating"
      :style="floatingStyles"
      class="w-sm fixed relative z-50"
    >
      <div
        class="relative inline-block max-w-full whitespace-normal break-words rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-normal text-gray-600 shadow-lg"
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useFloating, offset, flip, shift, autoUpdate } from "@floating-ui/vue";

interface Props {
  delayDuration?: number;
  skipDelayDuration?: number;
  placement?:
    | "top"
    | "top-start"
    | "top-end"
    | "right"
    | "right-start"
    | "right-end"
    | "bottom"
    | "bottom-start"
    | "bottom-end"
    | "left"
    | "left-start"
    | "left-end";
  maxWidth?: string;
}

const props = withDefaults(defineProps<Props>(), {
  delayDuration: 700,
  skipDelayDuration: 300,
  placement: "top",
  maxWidth: "300px",
});

const showTooltip = ref(false);
let showTimeout: number | null = null;
let lastShowTime = 0;

const triggerWrapper = ref<HTMLElement | null>(null);
const floating = ref<HTMLElement | null>(null);

const { floatingStyles, middlewareData } = useFloating(
  triggerWrapper,
  floating,
  {
    placement: props.placement,
    strategy: "fixed",
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
    open: showTooltip,
  },
);

const handleMouseEnter = () => {
  const now = Date.now();
  const timeSinceLastShow = now - lastShowTime;

  if (timeSinceLastShow < props.skipDelayDuration) {
    showTooltip.value = true;
    return;
  }

  showTimeout = window.setTimeout(() => {
    showTooltip.value = true;
    lastShowTime = Date.now();
  }, props.delayDuration);
};

const handleMouseLeave = () => {
  if (showTimeout) {
    clearTimeout(showTimeout);
    showTimeout = null;
  }
  showTooltip.value = false;
};

onUnmounted(() => {
  if (showTimeout) {
    clearTimeout(showTimeout);
  }
});
</script>
